import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card, Chip, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from "@mui/material";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";
import UserIdentityPreview from "components/UserIdentityPreview";
import { MarketingCallStatusSelect } from "components/MarketingCallStatus";

const PAGE_SIZES = [10, 25, 50, 100];
const PERIOD_OPTIONS = [
  { value: "month", label: "Month" },
  { value: "day", label: "Day" },
  { value: "date_range", label: "Date Range" },
  { value: "year", label: "Year" },
];
const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "success", label: "Success" },
  { value: "pending", label: "Pending" },
  { value: "ongoing", label: "Ongoing" },
  { value: "failed", label: "Failed" },
];

const padTwo = (value) => String(value).padStart(2, "0");

const getCurrentValues = () => {
  const now = new Date();
  const dateParts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
    .formatToParts(now)
    .reduce((parts, item) => ({ ...parts, [item.type]: item.value }), {});
  const year = dateParts.year;
  const month = padTwo(dateParts.month);
  const day = padTwo(dateParts.day);
  return {
    day: `${year}-${month}-${day}`,
    month: `${year}-${month}`,
    year: String(year),
  };
};

const getDefaultFilters = () => {
  const current = getCurrentValues();
  return {
    search: "",
    status: "all",
    period: "month",
    value: current.month,
    start_date: current.day,
    end_date: current.day,
  };
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrency = (amount, currency = "INR") => {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(Number(amount || 0));
  } catch (error) {
    return `${currency} ${Number(amount || 0).toFixed(2)}`;
  }
};

const titleize = (value) =>
  String(value || "-")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getUserImageUrl = (value) => {
  if (!value) return "";
  return String(value).startsWith("http") ? value : `${Constants.imageDomain}${value}`;
};

const statusSx = (status) => {
  const tones = {
    success: { color: "#4ade80", backgroundColor: "rgba(34,197,94,0.14)" },
    pending: { color: "#fbbf24", backgroundColor: "rgba(245,158,11,0.14)" },
    ongoing: { color: "#38bdf8", backgroundColor: "rgba(56,189,248,0.14)" },
    failed: { color: "#fb7185", backgroundColor: "rgba(244,63,94,0.14)" },
  };
  const tone = tones[status] || tones.pending;
  return {
    ...tone,
    border: `1px solid ${tone.color}66`,
    fontWeight: 700,
    textTransform: "capitalize",
  };
};

function Transactions() {
  const defaults = useMemo(getDefaultFilters, []);
  const [filters, setFilters] = useState(defaults);
  const [appliedFilters, setAppliedFilters] = useState(defaults);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    totalDocs: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [updatingCallStatus, setUpdatingCallStatus] = useState({});
  const [notification, setNotification] = useState({
    open: false,
    color: "warning",
    message: "",
  });

  const showNotice = (message, color = "warning") => {
    setNotification({ open: true, color, message });
  };

  useEffect(() => {
    if (init() !== "success") return undefined;

    setLoading(true);
    axios
      .get(Constants.getUrls.getTransactions, {
        params: {
          ...appliedFilters,
          search: appliedFilters.search.trim(),
          value: appliedFilters.period === "date_range" ? undefined : appliedFilters.value,
          start_date:
            appliedFilters.period === "date_range" ? appliedFilters.start_date : undefined,
          end_date: appliedFilters.period === "date_range" ? appliedFilters.end_date : undefined,
          page,
          limit,
        },
      })
      .then((response) => {
        const data = response?.data?.data || {};
        setRows(data.docs || []);
        setPagination({
          page: data.page || page,
          limit: data.limit || limit,
          totalDocs: data.totalDocs || 0,
          totalPages: data.totalPages || 1,
        });
      })
      .catch((error) => {
        setRows([]);
        showNotice(error?.response?.data?.message || "Unable to load transactions.", "error");
      })
      .finally(() => setLoading(false));

    return undefined;
  }, [appliedFilters, page, limit]);

  const handlePeriodChange = (period) => {
    const current = getCurrentValues();
    setFilters((previous) => ({
      ...previous,
      period,
      value:
        period === "day" ? current.day : period === "year" ? current.year : current.month,
      start_date: current.day,
      end_date: current.day,
    }));
  };

  const applyFilters = () => {
    if (
      filters.period === "date_range" &&
      filters.start_date &&
      filters.end_date &&
      filters.start_date > filters.end_date
    ) {
      showNotice("From date cannot be after To date.", "error");
      return;
    }

    setPage(1);
    setAppliedFilters({ ...filters, search: filters.search.trim() });
  };

  const resetFilters = () => {
    const nextFilters = getDefaultFilters();
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setPage(1);
  };

  const updateUserCallStatus = async (userId, callStatus) => {
    if (!userId || init() !== "success") return;

    try {
      setUpdatingCallStatus((current) => ({ ...current, [userId]: true }));
      const response = await axios.put(
        `${Constants.putUrls.updateUserCallStatus}${userId}/call-status`,
        { call_status: callStatus }
      );
      const savedStatus = response?.data?.data?.marketing_call_status || callStatus;
      setRows((current) =>
        current.map((transaction) =>
          transaction.user?._id === userId
            ? {
                ...transaction,
                user: { ...transaction.user, marketing_call_status: savedStatus },
              }
            : transaction
        )
      );
      showNotice(response?.data?.message || "Call status updated successfully.", "success");
    } catch (error) {
      showNotice(error?.response?.data?.message || "Unable to update call status.", "error");
    } finally {
      setUpdatingCallStatus((current) => ({ ...current, [userId]: false }));
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              All Transactions
            </MDTypography>
            <MDTypography variant="caption" color="white" opacity={0.8}>
              Purchases, wallet activity, refunds, rewards, and payment attempts
            </MDTypography>
          </MDBox>

          <MDBox px={2} py={2.5}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="User name, mobile, email, or transaction ID"
                  value={filters.search}
                  onChange={(event) =>
                    setFilters((previous) => ({ ...previous, search: event.target.value }))
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Enter") applyFilters();
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={filters.status}
                    onChange={(event) =>
                      setFilters((previous) => ({ ...previous, status: event.target.value }))
                    }
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Period</InputLabel>
                  <Select
                    label="Period"
                    value={filters.period}
                    onChange={(event) => handlePeriodChange(event.target.value)}
                  >
                    {PERIOD_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {filters.period === "date_range" ? (
                <>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      type="date"
                      label="From"
                      InputLabelProps={{ shrink: true }}
                      value={filters.start_date}
                      onChange={(event) =>
                        setFilters((previous) => ({
                          ...previous,
                          start_date: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      type="date"
                      label="To"
                      InputLabelProps={{ shrink: true }}
                      value={filters.end_date}
                      onChange={(event) =>
                        setFilters((previous) => ({
                          ...previous,
                          end_date: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type={
                      filters.period === "day"
                        ? "date"
                        : filters.period === "year"
                        ? "number"
                        : "month"
                    }
                    label={
                      filters.period === "day"
                        ? "Day"
                        : filters.period === "year"
                        ? "Year"
                        : "Month and Year"
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={filters.period === "year" ? { min: 2000, max: 2200 } : {}}
                    value={filters.value}
                    onChange={(event) =>
                      setFilters((previous) => ({ ...previous, value: event.target.value }))
                    }
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <MDBox display="flex" gap={1} flexWrap="wrap">
                  <MDButton color="info" onClick={applyFilters}>
                    Apply Filters
                  </MDButton>
                  <MDButton variant="outlined" color="secondary" onClick={resetFilters}>
                    Reset to Current Month
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>

          <MDBox pb={3}>
            {loading ? (
              <MDBox py={4} textAlign="center">
                <MDTypography variant="h6" sx={{ color: "#d7e5ff" }}>
                  Loading transactions...
                </MDTypography>
              </MDBox>
            ) : rows.length ? (
              <DataTable
                entriesPerPage={{ defaultValue: limit, entries: PAGE_SIZES }}
                showTotalEntries
                isSorted={false}
                table={{
                  columns: [
                    { Header: "#", accessor: "serial", width: "5%" },
                    { Header: "date", accessor: "date" },
                    { Header: "user", accessor: "user" },
                    { Header: "call status", accessor: "call_status" },
                    { Header: "transaction ID", accessor: "transaction_id" },
                    { Header: "purpose", accessor: "purpose" },
                    { Header: "amount", accessor: "amount" },
                    { Header: "status", accessor: "status" },
                    { Header: "gateway", accessor: "gateway" },
                  ],
                  rows: rows.map((transaction, index) => ({
                    serial: (pagination.page - 1) * pagination.limit + index + 1,
                    date: formatDate(transaction.createdAt),
                    user: transaction.user?._id ? (
                      <Link
                        to={`/user-details/${transaction.user._id}`}
                        style={{ textDecoration: "none" }}
                      >
                        <UserIdentityPreview
                          name={String(transaction.user.name || "").trim() || "-"}
                          imageUrl={getUserImageUrl(transaction.user.profile_pic_url)}
                          imageSize={38}
                        />
                        <MDTypography variant="caption" color="info" display="block" mt={0.5}>
                          View details
                        </MDTypography>
                      </Link>
                    ) : (
                      "-"
                    ),
                    call_status: transaction.user?._id ? (
                      <MarketingCallStatusSelect
                        status={transaction.user.marketing_call_status}
                        disabled={Boolean(updatingCallStatus[transaction.user._id])}
                        onChange={(callStatus) =>
                          updateUserCallStatus(transaction.user._id, callStatus)
                        }
                        ariaLabel={`Call status for ${transaction.user.name || "user"}`}
                      />
                    ) : (
                      "-"
                    ),
                    transaction_id: transaction.transaction_id || "-",
                    purpose: (
                      <MDBox>
                        <MDTypography variant="button" sx={{ color: "#edf4ff" }}>
                          {transaction.reference_name || titleize(transaction.reference_type)}
                        </MDTypography>
                        <MDTypography variant="caption" display="block" sx={{ color: "#8fa7c4" }}>
                          {titleize(transaction.reference_type)} • {titleize(transaction.payment_for)}
                        </MDTypography>
                      </MDBox>
                    ),
                    amount: formatCurrency(transaction.amount, transaction.currency),
                    status: (
                      <Chip
                        size="small"
                        label={
                          transaction.status_detail || transaction.payment_status || "pending"
                        }
                        sx={statusSx(transaction.payment_status)}
                      />
                    ),
                    gateway: (
                      <MDBox>
                        <MDTypography variant="button" sx={{ color: "#edf4ff" }}>
                          {titleize(transaction.gateway_name || transaction.pricing_type)}
                        </MDTypography>
                        <MDTypography variant="caption" display="block" sx={{ color: "#8fa7c4" }}>
                          {transaction.gateway_payment_id || transaction.gateway_order_id || "-"}
                        </MDTypography>
                      </MDBox>
                    ),
                  })),
                }}
                page_no={pagination.page}
                totalPages={pagination.totalPages}
                totalDocs={pagination.totalDocs}
                onPageChange={setPage}
                onEntriesPerPageChange={(nextLimit) => {
                  setLimit(nextLimit);
                  setPage(1);
                }}
              />
            ) : (
              <MDBox px={3} py={3}>
                <MDTypography variant="button" sx={{ color: "#c7d8eb" }}>
                  No transactions found for the selected filters.
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </Card>
      </MDBox>

      <MDSnackbar
        color={notification.color}
        icon="notifications"
        title="AgriQuiz"
        content={notification.message}
        dateTime="Just now"
        open={notification.open}
        close={() => setNotification((previous) => ({ ...previous, open: false }))}
      />
    </DashboardLayout>
  );
}

export default Transactions;
